"use client"

import { useRef, useState, type ChangeEvent } from "react"
import { Button } from "@/components/ui/button"
import { Upload, Trash2, Loader2 } from "lucide-react"

const DEFAULT_ACCEPT = "image/svg+xml,image/png,image/jpeg,image/webp"
const DEFAULT_MAX_SIZE_MB = 2

interface ImageUploadProps {
  currentImageUrl: string | null
  onUpload: (file: File) => Promise<void>
  onRemove: () => Promise<void>
  isUploading?: boolean
  accept?: string
  maxSizeMB?: number
  shape?: "circle" | "auto"
  previewClassName?: string
}

export function ImageUpload({
  currentImageUrl,
  onUpload,
  onRemove,
  isUploading = false,
  accept = DEFAULT_ACCEPT,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  shape = "auto",
  previewClassName,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset for next selection
    e.target.value = ""

    // Validate type
    const acceptedTypes = accept.split(",").map((t) => t.trim())
    if (!acceptedTypes.includes(file.type)) {
      setError(`Unsupported file type. Accepted: ${acceptedTypes.map((t) => t.split("/")[1]).join(", ")}`)
      return
    }

    // Validate size
    const maxBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxBytes) {
      setError(`File too large. Maximum size: ${maxSizeMB}MB`)
      return
    }

    setError(null)
    await onUpload(file)
  }

  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-lg"
  const defaultPreviewClass = `h-16 w-auto max-w-[200px] object-contain ${shapeClass}`

  return (
    <div className="space-y-3">
      {currentImageUrl && (
        <div className="flex items-center gap-4">
          <img
            src={currentImageUrl}
            alt="Current logo"
            className={previewClassName ?? defaultPreviewClass}
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange}
          disabled={isUploading}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          <span>{isUploading ? "Uploading..." : "Upload"}</span>
        </Button>

        {currentImageUrl && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={onRemove}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span>Remove</span>
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <p className="text-xs text-muted-foreground">
        <span>SVG, PNG, JPG or WEBP. Max</span> {maxSizeMB}<span>MB.</span>
      </p>
    </div>
  )
}
